import axios, { AxiosHeaders, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import toast from 'react-hot-toast';
import { auth } from '../config/firebase';
import { queryClient } from '../lib/queryClient';

class ApiService {
  private api: AxiosInstance;
  private pendingRequests: Map<string, Promise<any>> = new Map();
  private requestCounts: Map<string, number> = new Map();
  private lastRequestTime: Map<string, number> = new Map();
  private requestQueue: Array<() => Promise<any>> = [];
  private isProcessingQueue = false;
  private circuitBreakerOpen = false;
  private circuitBreakerFailures = 0;
  // Simplified - no aggressive throttling needed for simple forms
  private readonly MAX_REQUESTS_PER_SECOND = 1000; // Very high limit
  private readonly REQUEST_COOLDOWN = 10; // Minimal cooldown
  private readonly MAX_FAILURES = 1000; // Very high failure threshold
  private readonly CIRCUIT_BREAKER_TIMEOUT = 5000; // 5 seconds

  constructor() {
    this.api = axios.create({
      baseURL: (import.meta as any).env?.VITE_API_URL || 'http://localhost:10201/api',
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true, // Important for CORS with credentials
    });

    // Request interceptor to add auth token
    // Prefer fresh Firebase ID token over localStorage token to ensure we always use the latest token
    this.api.interceptors.request.use(
      async (config) => {
        // Try to get fresh token from Firebase first (for Google sign-in users)
        let token: string | null = null;
        const currentUser = auth.currentUser;
        if (currentUser) {
          try {
            token = await currentUser.getIdToken();
          } catch (error) {
            console.warn('Failed to get Firebase ID token, falling back to localStorage:', error);
          }
        }
        
        // Fall back to localStorage token if Firebase user is not available
        if (!token) {
          token = localStorage.getItem('token');
        }
        
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        // Automatically invalidate React Query cache when data is modified
        const method = response.config.method?.toUpperCase();
        if (method && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
          const url = response.config.url || '';
          this.invalidateQueriesByUrl(url);
        }
        return response;
      },
      (error) => {
        // Only show toast for certain errors to avoid spam
        if (error.response?.status === 403) {
          toast.error('Access denied. Insufficient permissions.');
        } else if (error.response?.status === 404) {
          // Don't show toast for 404s as they might be expected
          console.log('Resource not found:', error.config?.url);
        } else if (error.response?.status >= 500) {
          toast.error('Server error. Please try again later.');
        } else if (error.code === 'ECONNABORTED') {
          toast.error('Request timeout. Please try again.');
        } else if (!error.response && error.code !== 'ERR_CANCELED') {
          // Don't show toast for network errors that might be retries
          console.log('Network error:', error.message);
        }

        return Promise.reject(error);
      }
    );
  }

  private invalidateQueriesByUrl(url: string): void {
    try {
      // Invalidate queries based on resource type detected in the URL
      if (url.includes('/assessments') || url.includes('/assessment-goals') || url.includes('/collaboration')) {
        queryClient.invalidateQueries(['assessments']);
        queryClient.invalidateQueries(['assessment']);
        queryClient.invalidateQueries(['assessmentNotes']);
        queryClient.invalidateQueries(['maturityAnalysis']);
      }
      if (url.includes('/clients')) {
        queryClient.invalidateQueries(['clients']);
        queryClient.invalidateQueries(['client']);
      }
      if (url.includes('/users')) {
        queryClient.invalidateQueries(['users']);
        queryClient.invalidateQueries(['user']);
      }
      if (url.includes('/questionnaires') || url.includes('/questions') || url.includes('/question-branches')) {
        queryClient.invalidateQueries(['questionnaires']);
        queryClient.invalidateQueries(['questionnaire']);
        queryClient.invalidateQueries(['questions']);
        queryClient.invalidateQueries(['question']);
        queryClient.invalidateQueries(['question-branches']);
      }
      if (url.includes('/responses') || url.includes('/save-response')) {
        queryClient.invalidateQueries(['assessments']);
        queryClient.invalidateQueries(['assessment']);
        queryClient.invalidateQueries(['responses']);
        queryClient.invalidateQueries(['questionnaire']);
      }
    } catch (error) {
      console.error('Failed to invalidate queries on API change:', error);
    }
  }

  private getRequestKey(method: string, url: string, data?: any): string {
    return `${method}:${url}:${data ? JSON.stringify(data) : ''}`;
  }

  private shouldThrottleRequest(key: string): boolean {
    const now = Date.now();
    const lastTime = this.lastRequestTime.get(key) || 0;
    const count = this.requestCounts.get(key) || 0;

    // Check circuit breaker first
    if (this.circuitBreakerOpen) {
      console.warn(`Circuit breaker is open, blocking request to ${key}`);
      return true;
    }

    // Reset counter every second
    if (now - lastTime > 1000) {
      this.requestCounts.set(key, 0);
    }

    // Only throttle if we're hitting very high rate limits
    if (count >= this.MAX_REQUESTS_PER_SECOND) {
      console.warn(`Rate limit exceeded for ${key}, throttling request`);
      return true;
    }

    // Only check cooldown for identical requests within 100ms
    if (now - lastTime < this.REQUEST_COOLDOWN) {
      console.warn(`Request cooldown active for ${key}, throttling request`);
      return true;
    }

    return false;
  }

  private handleRequestFailure(): void {
    this.circuitBreakerFailures++;
    if (this.circuitBreakerFailures >= this.MAX_FAILURES) {
      this.circuitBreakerOpen = true;
      console.error(`Circuit breaker opened after ${this.circuitBreakerFailures} failures`);
      
      // Reset circuit breaker after timeout
      setTimeout(() => {
        this.circuitBreakerOpen = false;
        this.circuitBreakerFailures = 0;
        console.log('Circuit breaker reset, allowing requests again');
      }, this.CIRCUIT_BREAKER_TIMEOUT);
    }
  }

  private handleRequestSuccess(): void {
    this.circuitBreakerFailures = 0;
  }

  private updateRequestStats(key: string): void {
    const now = Date.now();
    const count = this.requestCounts.get(key) || 0;
    this.requestCounts.set(key, count + 1);
    this.lastRequestTime.set(key, now);
  }

  private async processQueue(): Promise<void> {
    if (this.isProcessingQueue || this.requestQueue.length === 0) {
      return;
    }

    this.isProcessingQueue = true;

    while (this.requestQueue.length > 0) {
      const request = this.requestQueue.shift();
      if (request) {
        try {
          await request();
          this.handleRequestSuccess();
        } catch (error) {
          console.warn('Request in queue failed:', error);
          this.handleRequestFailure();
        }
        // Minimal delay for simple forms
        await new Promise(resolve => setTimeout(resolve, 1));
      }
    }

    this.isProcessingQueue = false;
  }

  private queueRequest<T>(requestFn: () => Promise<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      this.requestQueue.push(async () => {
        try {
          const result = await requestFn();
          resolve(result);
        } catch (error) {
          reject(error);
        }
      });
      this.processQueue();
    });
  }

      async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        const key = this.getRequestKey('GET', url);
        
        // Check for pending identical requests first
        if (this.pendingRequests.has(key)) {
          return this.pendingRequests.get(key)!;
        }

        // Check if request should be throttled
        if (this.shouldThrottleRequest(key)) {
          console.warn(`Throttling GET request to ${url}`);
          return Promise.reject(new Error('Request throttled due to rate limiting'));
        }

        // Update request statistics
        this.updateRequestStats(key);

        // Queue the request
        const request = this.queueRequest(async () => {
          const response = await this.api.get<T>(url, config);
          return response.data;
        });

        this.pendingRequests.set(key, request);
        
        // Clean up when request completes
        request.finally(() => {
          this.pendingRequests.delete(key);
        });

        return request;
      }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const key = this.getRequestKey('POST', url, data);
    
    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key)!;
    }

    const request = this.api.post<T>(url, data, config).then(response => response.data).finally(() => {
      this.pendingRequests.delete(key);
    });

    this.pendingRequests.set(key, request);
    return request;
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const key = this.getRequestKey('PUT', url, data);
    
    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key)!;
    }

    const request = this.api.put<T>(url, data, config).then(response => response.data).finally(() => {
      this.pendingRequests.delete(key);
    });

    this.pendingRequests.set(key, request);
    return request;
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const key = this.getRequestKey('PATCH', url, data);
    
    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key)!;
    }

    const request = this.api.patch<T>(url, data, config).then(response => response.data).finally(() => {
      this.pendingRequests.delete(key);
    });

    this.pendingRequests.set(key, request);
    return request;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const key = this.getRequestKey('DELETE', url);
    
    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key)!;
    }

    const request = this.api.delete<T>(url, config).then(response => response.data).finally(() => {
      this.pendingRequests.delete(key);
    });

    this.pendingRequests.set(key, request);
    return request;
  }

  /**
   * POST multipart FormData (e.g. file + fields).
   * The axios instance defaults to Content-Type: application/json; that breaks multipart
   * (multer never sees `file`). Strip Content-Type so the browser sets multipart boundary.
   */
  async postFormData<T>(url: string, formData: FormData): Promise<T> {
    const response = await this.api.post<T>(url, formData, {
      transformRequest: [
        (data, headers) => {
          if (data instanceof FormData && headers) {
            if (headers instanceof AxiosHeaders) {
              headers.delete('Content-Type');
            } else {
              delete (headers as Record<string, unknown>)['Content-Type'];
              delete (headers as Record<string, unknown>)['content-type'];
            }
          }
          return data;
        },
      ],
    });
    return response.data;
  }

  async getBlob(url: string): Promise<Blob> {
    const response = await this.api.get(url, { responseType: 'blob' });
    return response.data as Blob;
  }

  // File upload method
  async uploadFile<T>(url: string, file: File, onProgress?: (progress: number) => void): Promise<T> {
    const formData = new FormData();
    formData.append('file', file);

    const config: AxiosRequestConfig = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    };

    const response = await this.api.post<T>(url, formData, config);
    return response.data;
  }
}

export const apiService = new ApiService();
